import { Add, Delete, Edit, Inventory2, MoreVert, Save, TableChart } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box, Button, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Modal, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import { AppPageHeader } from '../../Components/ui'
import { GridApi } from '@mui/x-data-grid'
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel'
import { useEffect, useRef, useState } from 'react'
import { getColumns } from '../../Components/ColumnsDefinitions/ProductsColumns'
import { CustomQuickFilter, CustomToolbarContainer, StyledDataGrid } from '../../Components/DataGrid'
import EmptyState from '../../Components/EmptyState'
import ModalHeader from '../../Components/Modal/ModalHeader'
import { Product } from '../../Model/ProductModel'
import { PriceTable } from '../../Model/PriceTableModel'
import { useDeleteProductsMutation, useFindAllProductsQuery, useUpdateProductsMutation } from '../../service/ProductService'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setProducts } from '../../store/reducers/product'
import ModalCRUDPriceTable from './ModalCRUDPriceTable'
import ProductsModal from './ModalProducts'
import { useFindAllPlaylistsQuery } from '../../service/PlaylistService'
import ProductsHelper from './Helper'
import { useSyncPriceTables } from './useSyncPriceTables'

const notSelectTable: PriceTable = { id: null, userId: null, name: 'Nenhuma Tabela' }

const ProductsPage = () => {
  const theme    = useTheme()
  const isDark   = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [openImport, setOpenImport]           = useState(false)
  const [openCRUD, setOpenCRUD]               = useState(false)
  const [openHelp, setOpenHelp]               = useState(false)
  const [moreAnchor, setMoreAnchor]           = useState<null | HTMLElement>(null)
  const [tableAnchor, setTableAnchor]         = useState<null | HTMLElement>(null)
  const [selectedProducts, setSelectedProducts] = useState<number[]>([])
  const [hasModifications, setHasModifications] = useState(false)
  const [selectTables, setSelectTables]       = useState<PriceTable[]>([])

  const dataGridRef          = useRef<GridApi | null>(null)
  const updatedProductsRef   = useRef<Product[]>([])

  const dispatch    = useAppDispatch()
  const products    = useAppSelector(state => state.product)
  const user        = useAppSelector(state => state.auth.user)
  const priceTables = useAppSelector(state => state.priceTable)
  useFindAllPlaylistsQuery(user?.id?.toString() || '')
  useFindAllProductsQuery(user?.id?.toString() || '', { refetchOnMountOrArgChange: true })
  const [deleteProducts, { isLoading: isDeleting }] = useDeleteProductsMutation()
  const [updateProducts, { isLoading: isUpdating }] = useUpdateProductsMutation()
  const syncPriceTables = useSyncPriceTables()

  useEffect(() => {
    setSelectTables(prev => [...prev, ...priceTables, notSelectTable])
  }, [priceTables])

  async function handleDelete() { await deleteProducts(selectedProducts) }

  function handleBulkAssignTable(tableId: number | null) {
    setTableAnchor(null)
    const newProducts = products.map(product => {
      if (product.id !== null && selectedProducts.includes(product.id)) {
        const updated = { ...product, priceTableId: tableId }
        updatedProductsRef.current = updatedProductsRef.current.filter(p => p.id !== updated.id)
        updatedProductsRef.current.push(updated)
        return updated
      }
      return product
    })
    dispatch(setProducts(newProducts))
    setHasModifications(true)
  }

  async function handleSave() {
    const updated = updatedProductsRef.current
    const result = await updateProducts(updated)
    setHasModifications(false)
    if ('error' in result) return
    updatedProductsRef.current = []
    const affectedIds = new Set(
      updated.map(p => p.priceTableId).filter((id): id is number => id != null)
    )
    await syncPriceTables(affectedIds)
  }

  function handleSelectionChange(selectionModel: GridRowSelectionModel) {
    setSelectedProducts(selectionModel as number[])
  }

  function handleCellClick(params: { field: string; id: any }) {
    if (params.field === 'priceTableId')
      dataGridRef.current?.startCellEditMode({ id: params.id, field: params.field })
  }

  function handleRowUpdate(newRow: Product, oldRow: Product) {
    const newProducts = products.map(product => {
      if (product.id !== null && selectedProducts.includes(product.id)) {
        const updatedProduct = { ...product, priceTableId: newRow.priceTableId }
        updatedProductsRef.current = updatedProductsRef.current.filter(p => p.id !== updatedProduct.id)
        updatedProductsRef.current.push(updatedProduct)
        return updatedProduct
      }
      return product.id === newRow.id ? newRow : product
    })
    updatedProductsRef.current = updatedProductsRef.current.filter(p => p.id !== newRow.id)
    updatedProductsRef.current.push(newRow)
    dispatch(setProducts(newProducts))
    setHasModifications(true)
    setSelectedProducts([])
    return newRow
  }

  const bdr  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
  const teal = isDark ? '#14b8a0' : '#0d9488'

  const SearchToolbar = () => (
    <CustomToolbarContainer sx={{ height: 48, display: 'flex', alignItems: 'center', px: 1 }}>
      <CustomQuickFilter placeholder="Buscar produto..." variant="filled" />
    </CustomToolbarContainer>
  )

  // ─── Modais compartilhados ────────────────────────────────────────────────
  const modals = (
    <>
      <ModalCRUDPriceTable open={openCRUD} onClose={() => setOpenCRUD(false)} />
      <ProductsModal productState={products} open={openImport} handleClose={() => setOpenImport(false)} />
      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 2, p: 4,
          maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto',
        }}>
          <ModalHeader title="Produtos" onClose={() => setOpenHelp(false)} />
          <ProductsHelper />
        </Box>
      </Modal>
    </>
  )

  // ─── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {modals}

        {/* Header compacto */}
        <Box sx={{
          px: 2, py: 1.25,
          borderBottom: `1px solid ${bdr}`,
          display: 'flex', alignItems: 'center', gap: 1,
          minHeight: 48, flexShrink: 0,
          bgcolor: 'background.default',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Produtos
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1 }}>
              {products.length} itens
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Atribuir tabela — visível quando há seleção */}
          {selectedProducts.length > 0 && (
            <>
              <Button
                size="small" variant="outlined"
                onClick={e => setTableAnchor(e.currentTarget)}
                startIcon={<TableChart sx={{ fontSize: 15 }} />}
                sx={{
                  height: 32, fontSize: 12, px: 1.5, borderRadius: '8px',
                  borderColor: isDark ? 'rgba(20,184,160,0.4)' : 'rgba(13,148,136,0.4)',
                  color: teal,
                }}
              >
                Tabela ({selectedProducts.length})
              </Button>

              <Menu
                anchorEl={tableAnchor}
                open={Boolean(tableAnchor)}
                onClose={() => setTableAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    borderRadius: '12px', minWidth: 180, maxHeight: 320,
                    boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                    border: `1px solid ${bdr}`,
                    overflowY: 'auto',
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${bdr}` }}>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Atribuir a tabela
                  </Typography>
                </Box>
                {priceTables.map(t => (
                  <MenuItem key={t.id} onClick={() => handleBulkAssignTable(t.id)} sx={{ py: 1.25, px: 2 }}>
                    <ListItemText primary={<Typography sx={{ fontSize: 14 }}>{t.name}</Typography>} />
                  </MenuItem>
                ))}
                <MenuItem onClick={() => handleBulkAssignTable(null)}
                  sx={{ py: 1.25, px: 2, borderTop: `1px solid ${bdr}` }}>
                  <ListItemText primary={<Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Nenhuma tabela</Typography>} />
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Salvar — visível só quando há modificações */}
          {hasModifications && (
            <LoadingButton
              size="small" color="success" variant="contained"
              loading={isUpdating} onClick={handleSave}
              startIcon={<Save />}
              sx={{ height: 32, fontSize: 12, px: 1.5, borderRadius: '8px' }}
            >
              Salvar
            </LoadingButton>
          )}

          {/* Excluir — visível só quando há seleção */}
          {selectedProducts.length > 0 && (
            <LoadingButton
              size="small" color="error" variant="outlined"
              loading={isDeleting} onClick={handleDelete}
              sx={{ height: 32, minWidth: 36, px: 1, borderRadius: '8px', borderColor: 'error.main' }}
            >
              <Delete sx={{ fontSize: 16 }} />
            </LoadingButton>
          )}

          {/* Menu mais opções */}
          <IconButton size="small" onClick={e => setMoreAnchor(e.currentTarget)}
            sx={{ width: 36, height: 36, color: 'text.disabled' }}>
            <MoreVert sx={{ fontSize: 18 }} />
          </IconButton>

          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                borderRadius: '12px', minWidth: 200,
                boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${bdr}`,
              },
            }}
          >
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenImport(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><Add sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Importar com TXT</Typography>} />
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenCRUD(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><TableChart sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Minhas Tabelas</Typography>} />
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenHelp(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><Edit sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Ajuda</Typography>} />
            </MenuItem>
          </Menu>
        </Box>

        {/* DataGrid */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 1, pb: 1 }}>
          <StyledDataGrid
            sx={{
              width: '100%',
              border: 'none',
              borderRadius: 0,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: 12,
                fontWeight: 600,
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${bdr}`,
                px: 1.5,
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: 44,
                borderTop: `1px solid ${bdr}`,
              },
            }}
            rows={products}
            columns={getColumns(selectTables)[1]}
            getRowId={row => row.id}
            rowHeight={44}
            columnHeaderHeight={36}
            initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
            pageSizeOptions={[20, 50, 100]}
            keepNonExistentRowsSelected
            disableRowSelectionOnClick
            rowSelectionModel={selectedProducts}
            onRowSelectionModelChange={handleSelectionChange}
            processRowUpdate={handleRowUpdate}
            checkboxSelection
            slots={{
              toolbar: SearchToolbar,
              noRowsOverlay: () => (
                <EmptyState icon={Inventory2} title="Nenhum produto cadastrado"
                  subtitle="Importe um arquivo TXT via menu" />
              ),
            }}
            slotProps={{ toolbar: { quickFilterProps: { debounceMs: 500 } } }}
            sortingOrder={['desc', 'asc']}
            onCellClick={handleCellClick}
          />
        </Box>
      </Box>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────────
  const DesktopToolbar = () => (
    <CustomToolbarContainer sx={{ height: 60, display: 'flex', alignItems: 'center', px: 1, gap: 1 }}>
      <CustomQuickFilter placeholder="Buscar itens..." variant="filled" />
      <LoadingButton color="success" variant="contained" disabled={!hasModifications}
        onClick={handleSave} loading={isUpdating} startIcon={<Save />}>
        Salvar
      </LoadingButton>
      <LoadingButton color="error" variant="contained" disabled={!selectedProducts.length}
        loading={isDeleting} onClick={handleDelete} startIcon={<Delete />}>
        Excluir
      </LoadingButton>
      <Button color="secondary" variant="contained" onClick={() => setOpenImport(true)}>
        <Add sx={{ mr: '3px' }} /> Importar com txt
      </Button>
      <Button color="secondary" variant="contained" onClick={() => setOpenCRUD(true)}>
        <Edit sx={{ mr: '3px' }} /> Minhas tabelas
      </Button>
    </CustomToolbarContainer>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {modals}

      <AppPageHeader
        title="Produtos"
        count={products.length}
        helpContent={<ProductsHelper />}
      />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 2, py: 1.5 }}>
        <StyledDataGrid
          sx={{ width: '100%' }}
          rows={products}
          columns={getColumns(selectTables)[0]}
          getRowId={row => row.id}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          pageSizeOptions={[10, 20, 30]}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          rowSelectionModel={selectedProducts}
          onRowSelectionModelChange={handleSelectionChange}
          processRowUpdate={handleRowUpdate}
          checkboxSelection
          density="compact"
          slots={{
            toolbar: DesktopToolbar,
            noRowsOverlay: () => (
              <EmptyState icon={Inventory2} title="Nenhum produto cadastrado"
                subtitle='Importe um arquivo TXT ou adicione manualmente' />
            ),
          }}
          slotProps={{ toolbar: { quickFilterProps: { debounceMs: 500 } } }}
          sortingOrder={['desc', 'asc']}
          onCellClick={handleCellClick}
        />
      </Box>
    </Box>
  )
}

export default ProductsPage
